// Date utilities — native Date / Intl based (no date-fns dependency).

import type { CalendarEvent } from "../types";
import { CLASSIFICATION_ORDER } from "./constants";
import { sortCalendarEvents } from "./sort";

/** Parse an ISO string into a Date, or null when invalid/empty. */
function parseIso(datetime: string | null | undefined): Date | null {
  if (!datetime) return null;
  const date = new Date(datetime);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format a datetime string for display (time only, 24h) in the local timezone.
 */
export function formatTime(datetime: string | null | undefined): string {
  const date = parseIso(datetime);
  if (!date) return "-";
  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Format a datetime string for display (full date) in the local timezone. */
export function formatDate(datetime: string | null | undefined): string {
  const date = parseIso(datetime);
  if (!date) return "-";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Local YYYY-MM-DD (for same-day comparison). */
function localIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Day-header label, e.g. "Mon, 14 Jan 2026" (local timezone). */
function dayLabel(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Today's date as YYYY-MM-DD in the viewer's LOCAL timezone — the calendar-v2
 *  default day. The day view is anchored to the viewer's local day, so "today"
 *  is the local date (not UTC). */
export function todayLocalDateString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Shift a YYYY-MM-DD date string by whole days, returning YYYY-MM-DD. Pure
 *  calendar arithmetic (timezone-agnostic) — handles month/year rollover. */
export function shiftDateString(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const d = new Date(year, month - 1, day + days, 0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** The UTC query window for a YYYY-MM-DD *local* day: local midnight (inclusive)
 *  to the next local midnight (exclusive), expressed as UTC ISO strings. So a
 *  23:30-local event on the selected day stays inside the window (not pushed to
 *  the next day), and the window matches the table's local-time grouping.
 *  `lt` (exclusive) keeps the next day's midnight event out. DST transitions are
 *  handled automatically — the window is simply the real 23/24/25-hour local day. */
export function localDayBoundsUtc(date: string): { gte: string; lt: string } {
  const [year, month, day] = date.split("-").map(Number);
  const start = new Date(year, month - 1, day, 0, 0, 0, 0); // local midnight
  const end = new Date(year, month - 1, day + 1, 0, 0, 0, 0); // next local midnight
  return { gte: start.toISOString(), lt: end.toISOString() };
}

/** Long-form label for a YYYY-MM-DD date, e.g. "Thursday, 10 September 2026". */
export function formatDayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export interface ClassificationGroup {
  classification: string;
  displayName: string;
  events: CalendarEvent[];
  nextEventUuid?: string;
}

export interface DayGroup {
  dayLabel: string;
  occursAt: string;
  classifications: ClassificationGroup[];
}

/** Human-readable display name for a classification. */
function classificationDisplayName(classification: string): string {
  const displayNames: Record<string, string> = {
    data: "Data Releases",
    speaker: "Central Bank & Regional Speakers",
    supply: "Auction / Supply",
    earning: "Earnings",
    expiry: "Expiries",
    ad_hoc: "Ad-Hoc Events",
    holiday: "Holidays",
  };
  return (
    displayNames[classification] ||
    classification.charAt(0).toUpperCase() + classification.slice(1)
  );
}

/**
 * Group events by day, then by classification within each day. Days are sorted
 * chronologically; classifications follow CLASSIFICATION_ORDER. On today's
 * group, the first future event of each classification is flagged as the "next
 * due" event.
 */
export function groupByDay(events: CalendarEvent[]): DayGroup[] {
  const grouped: Record<string, { occursAt: string; events: CalendarEvent[] }> = {};

  const sorted = sortCalendarEvents(events);

  for (const event of sorted) {
    const date = parseIso(event.occurs_at);
    if (!date) continue;
    const label = dayLabel(date);
    if (!grouped[label]) {
      grouped[label] = { occursAt: event.occurs_at as string, events: [] };
    }
    grouped[label].events.push(event);
  }

  const now = new Date();
  const todayStr = localIsoDate(now);

  return Object.entries(grouped)
    .map(([label, data]) => {
      const byClassification: Record<string, CalendarEvent[]> = {};
      for (const event of data.events) {
        const className = event.classification || "other";
        if (!byClassification[className]) byClassification[className] = [];
        byClassification[className].push(event);
      }

      const dayDate = parseIso(data.occursAt);
      const isToday = dayDate ? localIsoDate(dayDate) === todayStr : false;

      const classifications: ClassificationGroup[] = Object.keys(byClassification)
        .sort(
          (a, b) =>
            (CLASSIFICATION_ORDER[a] ?? 999) - (CLASSIFICATION_ORDER[b] ?? 999)
        )
        .map((className) => {
          const classEvents = byClassification[className];

          let nextEventUuid: string | undefined;
          if (isToday) {
            for (const event of classEvents) {
              const eventTime = parseIso(event.occurs_at);
              if (eventTime && eventTime > now && event.uuid) {
                nextEventUuid = event.uuid;
                break;
              }
            }
          }

          return {
            classification: className,
            displayName: classificationDisplayName(className),
            events: classEvents,
            nextEventUuid,
          };
        });

      return { dayLabel: label, occursAt: data.occursAt, classifications };
    })
    .sort(
      (a, b) => new Date(a.occursAt).getTime() - new Date(b.occursAt).getTime()
    );
}
